import React, {useState, useRef, useEffect} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {Plus, Calculator, Upload, GripVertical, Trash2} from 'lucide-react'
import axios from 'axios'
import CustomerSelectorField from './CustomerSelectorField'
import {
  createAccountCreditNote,
  updateAccountCreditNote,
  getAccountCreditNoteById,
  getAccountCreditNoteItems,
  createAccountCreditNoteItem,
  deleteAccountCreditNoteItem,
  getAccountInvoices,
  uploadMenuFiles,
  deleteMenuFile,
} from '../core/_requests'
import {setupAxios} from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'
const api = axios.create({baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || ''})
setupAxios(api)

type UploadedFile = {
  originalName: string
  fileName: string
  size: number
  mimeType?: string
  path: string
  url?: string
}

export default function AccountsNewCreditNote() {
  const navigate = useNavigate()
  const {id} = useParams()
  const creditNoteId = id ? Number(id) : null
  const isEditing = creditNoteId !== null && !Number.isNaN(creditNoteId)
  const [saving, setSaving] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)

  // Form State
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [selectedCustomerName, setSelectedCustomerName] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoices, setInvoices] = useState<any[]>([])
  const [creditNoteNumber, setCreditNoteNumber] = useState(`CN-${String(Date.now()).slice(-5)}`)
  const [referenceNumber, setReferenceNumber] = useState('')
  const [creditNoteDate, setCreditNoteDate] = useState(new Date().toISOString().slice(0, 10))
  const [expiryDate, setExpiryDate] = useState('')
  const [subject, setSubject] = useState('')

  useEffect(() => {
    getAccountInvoices()
      .then((data) => setInvoices(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to load invoices:', err))
  }, [])

  // Line Items
  const [items, setItems] = useState([
    {id: 1, dbId: null as number | null, description: '', quantity: '1.00', rate: '0.00'},
  ])

  // Totals & Settings
  const [customerNotes, setCustomerNotes] = useState('')
  const [termsConditions, setTermsConditions] = useState('')
  const [discountPercent, setDiscountPercent] = useState('0')
  const [taxType, setTaxType] = useState('TDS')
  const [taxRate, setTaxRate] = useState('')
  const [adjustment, setAdjustment] = useState('0')
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  const buildUploadedFiles = (raw: any): UploadedFile[] => {
    const entries = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : []

    return entries
      .map((entry) => (typeof entry === 'string' ? entry.replace(/\\/g, '/').trim() : ''))
      .filter(Boolean)
      .map((entry) => {
        const fileName = entry.split('/').pop() || entry
        let url = entry
        if (!/^https?:\/\//i.test(entry)) {
          const basePath = entry.startsWith('/') ? entry : `/${entry}`
          const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || ''
          url = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`
        }
        return {
          originalName: fileName,
          fileName,
          size: 0,
          mimeType: '',
          path: entry,
          url,
        } as UploadedFile
      })
  }

  const [loadingInitial, setLoadingInitial] = useState(false)

  useEffect(() => {
    if (!isEditing || creditNoteId == null) return
    let isActive = true
    setLoadingInitial(true)

    Promise.all([
      getAccountCreditNoteById(creditNoteId),
      getAccountCreditNoteItems(creditNoteId).catch(() => []),
    ])
      .then(([data, itemsData]) => {
        if (!isActive) return
        setSelectedCustomerId(data.customerId ? String(data.customerId) : '')
        setSelectedCustomerName(data.customerName || '')
        setInvoiceNumber(data.invoiceNumber || data.invoice_number || '')
        setCreditNoteNumber(data.creditNoteNumber || `CN-${String(Date.now()).slice(-5)}`)
        setReferenceNumber(data.referenceNumber || '')
        setCreditNoteDate(data.creditNoteDate || new Date().toISOString().slice(0, 10))
        setExpiryDate(data.expiryDate || '')
        setSubject(data.subject || '')
        setDiscountPercent(data.discountPercent ? String(data.discountPercent) : '0')
        if (data.taxType) setTaxType(data.taxType)

        const taxRateDerived =
          data.taxAmount && data.subTotal && data.subTotal > 0
            ? Math.abs(
                (data.taxAmount / Math.max(0, data.subTotal - (data.discountAmount || 0))) * 100
              ).toFixed(1)
            : ''
        setTaxRate(taxRateDerived || '')

        setAdjustment(data.adjustment ? String(data.adjustment) : '0')
        setCustomerNotes(data.customerNotes || '')
        setTermsConditions(data.termsConditions || '')

        const filePathValue =
          data.filePath ?? data.filePaths ?? data.file_path ?? data.filepath ?? ''
        const existingFiles = buildUploadedFiles(filePathValue)
        setUploadedFiles(existingFiles)

        if (itemsData && Array.isArray(itemsData) && itemsData.length > 0) {
          setItems(
            itemsData.map((item: any, index: number) => ({
              id: index + 1,
              dbId: item.id,
              description: item.itemName || item.description || '',
              quantity: item.quantity ? String(item.quantity) : '1.00',
              rate: item.rate ? String(item.rate) : '0.00',
            }))
          )
        }
      })
      .catch(console.error)
      .finally(() => {
        if (isActive) setLoadingInitial(false)
      })

    return () => {
      isActive = false
    }
  }, [isEditing, creditNoteId])

  // Calculations
  const toNumber = (val: any) => {
    const num = parseFloat(val)
    return Number.isFinite(num) ? num : 0
  }

  const getRowAmount = (row: any) => toNumber(row.quantity) * toNumber(row.rate)
  const subTotal = items.reduce((sum, row) => sum + getRowAmount(row), 0)
  const discountAmount = subTotal * (toNumber(discountPercent) / 100)
  const adjustmentAmount = toNumber(adjustment)
  const taxableBase = Math.max(0, subTotal - discountAmount + adjustmentAmount)

  const activeTaxOptions =
    taxType === 'TDS'
      ? [
          {label: 'Dividend [10%]', rate: 10},
          {label: 'Other Interest [10%]', rate: 10},
          {label: 'Contractors [2%]', rate: 2},
        ]
      : [{label: 'TCS on Goods [0.1%]', rate: 0.1}]

  const selectedTax = activeTaxOptions.find((opt) => opt.rate === Number(taxRate))
  const taxAmountRaw = taxableBase * ((selectedTax?.rate || 0) / 100)
  const taxAmount = taxType === 'TDS' ? -taxAmountRaw : taxAmountRaw
  const total = taxableBase + taxAmount

  const filteredInvoices = React.useMemo(() => {
    if (!selectedCustomerId) return []
    return invoices.filter((inv: any) => {
      const invCustId = inv.customerId ?? inv.customer_id
      return String(invCustId) === String(selectedCustomerId)
    })
  }, [invoices, selectedCustomerId])

  const hasCurrentInvoice = Boolean(
    invoiceNumber &&
      filteredInvoices.some(
        (inv: any) => (inv.invoiceNumber || inv.invoice_number) === invoiceNumber
      )
  )

  const handleSave = async (status = 'Draft') => {
    if (!creditNoteNumber) {
      alert('Please provide a credit note number')
      return
    }
    setSaving(true)
    const uploadedPaths = uploadedFiles.map((file) => file.path).filter(Boolean)
    try {
      const payload = {
        customerId: selectedCustomerId ? Number(selectedCustomerId) : null,
        customerName: selectedCustomerName,
        creditNoteNumber: creditNoteNumber,
        invoiceNumber: invoiceNumber || null,
        referenceNumber: referenceNumber,
        creditNoteDate: creditNoteDate || null,
        expiryDate: expiryDate || null,
        subject: subject,
        subTotal: subTotal,
        discountPercent: toNumber(discountPercent),
        discountAmount: discountAmount,
        taxType: taxType,
        taxName: selectedTax ? selectedTax.label : null,
        taxAmount: taxAmount,
        adjustment: adjustmentAmount,
        filePath: uploadedPaths.length ? uploadedPaths.join(',') : null,
        total: total,
        balance: total,
        customerNotes: customerNotes,
        termsConditions: termsConditions,
        status: status,
      }

      let finalId = creditNoteId

      if (isEditing && finalId) {
        await updateAccountCreditNote(finalId, payload)
      } else {
        const resp = await createAccountCreditNote(payload)
        finalId = resp.id || resp.data?.id
      }

      if (finalId) {
        const existingItems = await getAccountCreditNoteItems(finalId).catch(() => [])
        const existingItemIds = existingItems.map((i: any) => i.id)
        const currentItemDbIds = items.map((i) => i.dbId).filter((id) => id !== null)

        const itemsToDelete = existingItemIds.filter((id: number) => !currentItemDbIds.includes(id))
        for (const delId of itemsToDelete) {
          await deleteAccountCreditNoteItem(finalId, delId).catch(console.error)
        }

        for (let i = 0; i < items.length; i++) {
          const row = items[i]
          const itemPayload = {
            itemName: row.description,
            description: row.description,
            quantity: toNumber(row.quantity),
            rate: toNumber(row.rate),
            amount: getRowAmount(row),
            itemOrder: i,
          }
          await createAccountCreditNoteItem(finalId, itemPayload).catch(console.error)
        }
      }

      navigate('/accounts/sales/credit-notes')
    } catch (err: any) {
      console.error(err)
      alert('Failed to save credit note')
    } finally {
      setSaving(false)
    }
  }

  const addRow = () => {
    setItems((prev) => {
      const nextId = prev.reduce((max, row) => Math.max(max, row.id), 0) + 1
      return [...prev, {id: nextId, dbId: null, description: '', quantity: '1.00', rate: '0.00'}]
    })
  }

  const updateRow = (id: number, field: string, value: any) => {
    setItems((prev) => prev.map((row) => (row.id === id ? {...row, [field]: value} : row)))
  }

  const removeRow = (id: number) => {
    if (items.length > 1) {
      setItems((prev) => prev.filter((row) => row.id !== id))
    }
  }

  const triggerDocumentSelect = () => {
    documentInputRef.current?.click()
  }

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || ([] as File[]))
    if (!files.length) {
      return
    }

    setUploadingFiles(true)
    setUploadError(null)

    try {
      const uploaded = await uploadMenuFiles('credit_notes', files)
      const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || ''
      const formattedUploads = (uploaded as UploadedFile[]).map((f) => {
        let fileUrl = f.url || f.path
        if (!/^https?:\/\//i.test(fileUrl)) {
          const basePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`
          fileUrl = `${baseUrl}${
            basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`
          }`
        }
        return {...f, url: fileUrl}
      })
      setUploadedFiles((prev) => [...prev, ...formattedUploads])
    } catch (err) {
      const message = 'Upload failed.'
      setUploadError(message)
    } finally {
      setUploadingFiles(false)
      event.target.value = ''
    }
  }

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      if (file.fileName) {
        await deleteMenuFile('credit_notes', file.fileName)
      }
      setUploadedFiles((prev) => prev.filter((f) => f.path !== file.path))
    } catch (err) {
      const message = 'Delete failed.'
      setUploadError(message)
    }
  }

  const inputClass =
    'w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300'
  const labelClass = 'block text-[11px] font-bold text-slate-600 mb-1.5'

  if (loadingInitial) {
    return (
      <div className='flex h-screen w-full items-center justify-center bg-slate-50'>
        <p className='text-slate-500 font-medium'>Loading Credit Note...</p>
      </div>
    )
  }

  return (
    <>
      <div className='flex h-screen w-full overflow-hidden bg-slate-50'>
        <div id='sidebar-overlay' className='fixed inset-0 bg-black/50 z-40 hidden md:hidden'></div>
        <main className='flex-1 flex flex-col h-full overflow-hidden relative'>
          <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6' id='main-scroll'>
            <div className='max-w-5xl mx-auto pb-20'>
              <div className='mb-6 flex items-center justify-between'>
                <div>
                  <div className='text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5'>
                    <span
                      className='text-slate-400 hover:text-blue-600 cursor-pointer transition-colors'
                      onClick={() => navigate('/accounts')}
                    >
                      ACCOUNTS
                    </span>
                    <span className='text-slate-300'>/</span>
                    <span
                      className='text-slate-400 hover:text-blue-600 cursor-pointer transition-colors'
                      onClick={() => navigate('/accounts/sales')}
                    >
                      SALES
                    </span>
                    <span className='text-slate-300'>/</span>
                    <span className='text-blue-600'>CREDIT NOTES</span>
                  </div>
                  <h1 className='text-3xl font-extrabold text-slate-900 leading-tight'>
                    {isEditing ? `Edit Credit Note: ${creditNoteNumber}` : 'New Credit Note'}
                  </h1>
                  <p className='text-sm font-medium text-slate-500 mt-1'>
                    Create or edit a credit note for a customer
                  </p>
                </div>
              </div>

              <form
                ref={formRef}
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSave()
                }}
                className='bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8'
              >
                {/* Primary Details Section */}
                <div className='mb-8 max-w-4xl space-y-6'>
                  {/* Customer Name */}
                  <div className='flex flex-col md:flex-row md:items-center gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0'>
                      <label className='text-[13px] font-bold text-slate-700'>Customer Name</label>
                      <span className='text-red-500 font-bold'>*</span>
                    </div>
                    <div className='flex-1'>
                      <CustomerSelectorField
                        value={selectedCustomerId}
                        onChange={(id, name) => {
                          setSelectedCustomerId(id)
                          setSelectedCustomerName(name)
                        }}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Invoice# */}
                  <div className='flex flex-col md:flex-row md:items-center gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0'>
                      <label className='text-[13px] font-bold text-slate-700'>Invoice#</label>
                    </div>
                    <div className='flex-1'>
                      <SearchableSelect
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        className={inputClass}
                        disabled={!selectedCustomerId}
                      >
                        <option value=''>
                          {selectedCustomerId
                            ? 'Select an Invoice (optional)...'
                            : 'Select a customer first...'}
                        </option>
                        {!hasCurrentInvoice && invoiceNumber && (
                          <option value={invoiceNumber}>
                            {invoiceNumber} (Selected Reference)
                          </option>
                        )}
                        {filteredInvoices.map((inv: any) => {
                          const invNum = inv.invoiceNumber || inv.invoice_number
                          const invDate = inv.invoiceDate || inv.invoice_date
                          const invTotal = inv.total != null ? Number(inv.total).toFixed(2) : null
                          return (
                            <option key={inv.id} value={invNum}>
                              {invNum} {invDate ? `(${invDate})` : ''}{' '}
                              {invTotal ? `- $${invTotal}` : ''}
                            </option>
                          )
                        })}
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Credit Note# */}
                  <div className='flex flex-col md:flex-row md:items-center gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0'>
                      <label className='text-[13px] font-bold text-slate-700'>Credit Note#</label>
                      <span className='text-red-500 font-bold'>*</span>
                    </div>
                    <div className='flex-1 relative'>
                      <input
                        type='text'
                        value={creditNoteNumber}
                        onChange={(e) => setCreditNoteNumber(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Reference# */}
                  <div className='flex flex-col md:flex-row md:items-center gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0'>
                      <label className='text-[13px] font-bold text-slate-700'>Reference#</label>
                    </div>
                    <div className='flex-1'>
                      <input
                        type='text'
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Credit Note Date */}
                  <div className='flex flex-col md:flex-row md:items-center gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0'>
                      <label className='text-[13px] font-bold text-slate-700'>
                        Credit Note Date
                      </label>
                      <span className='text-red-500 font-bold'>*</span>
                    </div>
                    <div className='flex-1'>
                      <input
                        type='date'
                        value={creditNoteDate}
                        onChange={(e) => setCreditNoteDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div className='flex flex-col md:flex-row md:items-center gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0'>
                      <label className='text-[13px] font-bold text-slate-700'>Expiry Date</label>
                    </div>
                    <div className='flex-1'>
                      <input
                        type='date'
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className='flex flex-col md:flex-row md:items-start gap-4'>
                    <div className='md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5'>
                      <label className='text-[13px] font-bold text-slate-700'>Subject</label>
                    </div>
                    <div className='flex-1'>
                      <textarea
                        rows={2}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder='Let your customer know what this Credit Note is for'
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                  </div>
                </div>

                {/* Item Table */}
                <div className='mb-8 overflow-visible border border-slate-200 rounded-xl shadow-sm'>
                  <div className='bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between rounded-t-xl'>
                    <h3 className='text-sm font-bold text-slate-800'>Item Table</h3>
                  </div>

                  <div className='overflow-x-auto pb-24'>
                    <table className='w-full text-left border-collapse min-w-175'>
                      <thead>
                        <tr className='bg-white border-b border-slate-100'>
                          <th className='px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[45%]'>
                            Item Details
                          </th>
                          <th className='px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]'>
                            Quantity
                          </th>
                          <th className='px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[20%]'>
                            Rate <Calculator className='w-3 h-3 inline-block ml-1 opacity-50' />
                          </th>
                          <th className='px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]'>
                            Amount
                          </th>
                          <th className='px-4 py-3 w-[5%]'></th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-slate-100'>
                        {items.map((row) => (
                          <tr
                            key={row.id}
                            className='bg-white group hover:bg-slate-50/50 transition-colors'
                          >
                            <td className='px-4 py-3'>
                              <div className='flex items-start gap-3'>
                                <GripVertical className='w-4 h-4 text-slate-300 mt-2 cursor-grab' />
                                <textarea
                                  rows={1}
                                  value={row.description}
                                  onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                                  placeholder='Type or click to select an item.'
                                  className='w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all resize-none placeholder:text-slate-400'
                                ></textarea>
                              </div>
                            </td>
                            <td className='px-4 py-3 align-top'>
                              <input
                                type='text'
                                value={row.quantity}
                                onChange={(e) => updateRow(row.id, 'quantity', e.target.value)}
                                className='w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all'
                              />
                            </td>
                            <td className='px-4 py-3 align-top'>
                              <input
                                type='text'
                                value={row.rate}
                                onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                                className='w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all'
                              />
                            </td>
                            <td className='px-4 py-3 align-top text-right'>
                              <div className='pt-2 text-sm font-bold text-slate-700'>
                                {getRowAmount(row).toFixed(2)}
                              </div>
                            </td>
                            <td className='px-4 py-3 align-top text-center'>
                              {items.length > 1 && (
                                <button
                                  type='button'
                                  onClick={() => removeRow(row.id)}
                                  className='p-1.5 mt-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors'
                                >
                                  <Trash2 className='w-4 h-4' />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className='bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2 rounded-b-xl'>
                    <button
                      type='button'
                      onClick={addRow}
                      className='text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-100/50 hover:bg-blue-100 px-3 py-2 rounded-xl transition-colors'
                    >
                      <Plus className='w-3.5 h-3.5' /> Add New Row
                    </button>
                  </div>
                </div>

                {/* Additional Details & Totals */}
                <div className='grid grid-cols-1 lg:grid-cols-12 gap-12'>
                  <div className='lg:col-span-7 space-y-6'>
                    <div>
                      <label className={labelClass}>Customer Notes</label>
                      <textarea
                        rows={3}
                        value={customerNotes}
                        onChange={(e) => setCustomerNotes(e.target.value)}
                        placeholder='Will be displayed on the credit note'
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Terms & Conditions</label>
                      <textarea
                        rows={3}
                        value={termsConditions}
                        onChange={(e) => setTermsConditions(e.target.value)}
                        placeholder='Enter the terms and conditions'
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Attach File(s) to Credit Note</label>
                      <input
                        ref={documentInputRef}
                        type='file'
                        multiple
                        hidden
                        onChange={handleDocumentChange}
                      />
                      <button
                        type='button'
                        onClick={triggerDocumentSelect}
                        disabled={uploadingFiles}
                        className='flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm disabled:opacity-50'
                      >
                        <Upload className='w-4 h-4 text-slate-400' />{' '}
                        {uploadingFiles ? 'Uploading...' : 'Upload File'}
                      </button>
                      <p className='text-[11px] text-slate-400 mt-2 font-medium'>
                        Max 5 files, 10MB each
                      </p>
                      {uploadError && (
                        <p className='text-[11px] text-red-500 mt-1 font-medium'>{uploadError}</p>
                      )}
                      {uploadedFiles.length > 0 && (
                        <ul className='mt-2 space-y-1'>
                          {uploadedFiles.map((file) => (
                            <li
                              key={`${file.fileName}-${file.path || ''}`}
                              className='flex items-center gap-2 group'
                            >
                              <a
                                href={file.url || file.path}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline'
                              >
                                {file.originalName || file.fileName || file.path}
                              </a>
                              <button
                                type='button'
                                onClick={() => handleDeleteFile(file)}
                                className='text-slate-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors opacity-0 group-hover:opacity-100'
                                title='Delete file'
                              >
                                <Trash2 className='w-3.5 h-3.5' />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className='lg:col-span-5'>
                    <div className='bg-slate-50 border border-slate-200 rounded-2xl p-6'>
                      <div className='flex items-center justify-between mb-4'>
                        <span className='text-sm font-bold text-slate-700'>Sub Total</span>
                        <span className='text-sm font-bold text-slate-900'>
                          {subTotal.toFixed(2)}
                        </span>
                      </div>

                      <div className='flex items-center justify-between mb-4'>
                        <span className='text-sm font-semibold text-slate-500 w-24'>Discount</span>
                        <div className='flex-1 flex items-center justify-end gap-3'>
                          <div className='flex items-center w-24 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-50 transition-all'>
                            <input
                              type='text'
                              value={discountPercent}
                              onChange={(e) => setDiscountPercent(e.target.value)}
                              className='w-full text-right px-2 py-1.5 text-sm outline-none'
                            />
                            <div className='px-2 py-1.5 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500'>
                              %
                            </div>
                          </div>
                          <span className='text-sm font-medium text-slate-700 w-20 text-right'>
                            {discountAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className='flex items-center justify-between mb-4'>
                        <div className='flex items-center gap-3 w-32'>
                          <label className='flex items-center gap-1.5 cursor-pointer'>
                            <input
                              type='radio'
                              name='taxType'
                              checked={taxType === 'TDS'}
                              onChange={() => {
                                setTaxType('TDS')
                                setTaxRate('')
                              }}
                              className='w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300'
                            />
                            <span className='text-[12px] font-semibold text-slate-600'>TDS</span>
                          </label>
                          <label className='flex items-center gap-1.5 cursor-pointer'>
                            <input
                              type='radio'
                              name='taxType'
                              checked={taxType === 'TCS'}
                              onChange={() => {
                                setTaxType('TCS')
                                setTaxRate('')
                              }}
                              className='w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300'
                            />
                            <span className='text-[12px] font-semibold text-slate-600'>TCS</span>
                          </label>
                        </div>
                        <div className='flex-1 flex items-center justify-end gap-3'>
                          <SearchableSelect
                            value={taxRate}
                            onChange={(e) => setTaxRate(e.target.value)}
                            className='w-40 px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 transition-all'
                          >
                            {activeTaxOptions.length > 0 ? (
                              <>
                                <option value=''>Select a Tax</option>
                                {activeTaxOptions.map((opt) => (
                                  <option key={opt.label} value={String(opt.rate)}>
                                    {opt.label}
                                  </option>
                                ))}
                              </>
                            ) : (
                              <option value='' disabled>
                                No results found
                              </option>
                            )}
                          </SearchableSelect>
                          <span className='text-sm font-medium text-slate-700 w-20 text-right'>
                            {taxAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className='flex items-center justify-between mb-6'>
                        <span className='text-sm font-semibold text-slate-500 w-24'>
                          Adjustment
                        </span>
                        <div className='flex-1 flex items-center justify-end gap-3'>
                          <input
                            type='text'
                            value={adjustment}
                            onChange={(e) => setAdjustment(e.target.value)}
                            className='w-24 text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all'
                          />
                          <span className='text-sm font-medium text-slate-700 w-20 text-right'>
                            {adjustmentAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className='pt-4 border-t border-slate-200 flex items-center justify-between'>
                        <span className='text-lg font-black text-slate-900'>Total ($)</span>
                        <span className='text-xl font-black text-blue-600'>{total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className='pt-8 mt-8 border-t border-slate-100 flex items-center gap-3'>
                  <button
                    type='button'
                    onClick={() => handleSave('Draft')}
                    disabled={saving}
                    className='px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:shadow transition-all hover:bg-slate-50 disabled:opacity-50'
                  >
                    Save as Draft
                  </button>
                  <button
                    type='button'
                    onClick={() => handleSave('Open')}
                    disabled={saving}
                    className='px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50'
                  >
                    {saving ? 'Saving...' : 'Save and Send'}
                  </button>
                  <button
                    type='button'
                    onClick={() => navigate(-1)}
                    className='px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors'
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
